import React, { createContext, useCallback, useContext } from 'react';
import {
  Slider as MuiSlider,
  SlotComponentProps,
  SliderTrack,
  SliderTrackSlotPropsOverrides,
  type SliderProps as MuiSliderProps,
} from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import { SliderOwnerState } from '@mui/material';
import { useObjectMemo } from '@client/hooks/use-object.hooks';

type SliderColor = NonNullable<MuiSliderProps['color']>;
type SliderValueLabelDisplay = NonNullable<MuiSliderProps['valueLabelDisplay']>;
type TrackOrigin =
  /* track starts at min value */
  | 'start'
  /* track starts at min + (max - min) / 2 – center of range */
  | 'center'
  /* track starts at max value */
  | 'end'
  /* track starts at zero, bounded by min & max */
  | 'zero';
type OnChangeHandler = NonNullable<MuiSliderProps['onChange']>;
type OnChangeCommittedHandler = NonNullable<
  MuiSliderProps['onChangeCommitted']
>;

export type BaseSliderProps = {
  value: number;
  min: number;
  max: number;
  step?: MuiSliderProps['step'];
  // marks does not work with our custom track. we need to build special logic to make it work if we want it
  // marks?: MuiSliderProps['marks'];
  disabled?: boolean;
  valueLabelDisplay?: SliderValueLabelDisplay;
  color?: SliderColor;
  scale?: MuiSliderProps['scale'];
  valueLabelFormat?: MuiSliderProps['valueLabelFormat'];
  name?: string;
  id?: string;
  onBlur?: MuiSliderProps['onBlur'];
  onChange?: (
    event: Event | React.SyntheticEvent,
    value: number,
    activeThumb: number,
  ) => void;
  onChangeCommitted?: (
    event: Event | React.SyntheticEvent,
    value: number,
  ) => void;
  /* trackOrigin specifies where the filled track of the slider starts:
   * - 'start': at the minimum value
   * - 'center': at the midpoint between min and max
   * - 'end': at the maximum value
   * - 'zero': at the zero value, bounded by min & max.
   */
  trackOrigin?: TrackOrigin;
};

const useStyles = makeStyles<{ disabled: boolean }>()(
  (theme, { disabled }) => ({
    rail: {
      background: theme.palette.neutral.lightest,
      opacity: 1,
    },
    track: {
      background: disabled
        ? theme.palette.neutral.light
        : theme.palette.neutral.dark,
      transition: 'none',
    },
    thumb: {
      display: disabled ? 'none' : undefined,
      height: theme.spacing(1.5),
      width: theme.spacing(1.5),
      transition: 'none',
      background: theme.palette.neutral.dark,
      '&:hover, &.Mui-focusVisible': {
        boxShadow: `0 0 0 ${theme.spacing(0.5)} ${theme.palette.neutral.lighter}`,
      },
      '&.Mui-active': {
        boxShadow: `0 0 0 ${theme.spacing(1)} ${theme.palette.neutral.lighter}`,
      },
    },
    root: {
      padding: `${3}px 0`,
    },
  }),
);

type TrackSlotProps =
  | (SlotComponentProps<
      'span',
      SliderTrackSlotPropsOverrides,
      SliderOwnerState
    > & {
      ownerState?: SliderOwnerState;
      style?: React.CSSProperties;
      className?: string;
      trackOrigin?: TrackOrigin;
    })
  | undefined;

const getTrackStyle = (
  values: SliderContextValues,
): { left: `${number}%`; width: `${number}%` } => {
  const { trackOrigin: origin, value } = values;
  const min = Math.min(values.min, values.max);
  const max = Math.max(values.min, values.max);
  const rangeWidth = max - min;
  if (rangeWidth === 0) return { left: '0%', width: `0%` };

  const originValueLookup: Record<TrackOrigin, number> = {
    start: min,
    center: (min + max) / 2,
    end: max,
    zero: Math.min(max, Math.max(min, 0)),
  };
  const originValue = originValueLookup[origin];

  const originPercent = ((originValue - min) / rangeWidth) * 100;
  const clampedValue = Math.min(max, Math.max(min, value));
  const valuePercent = ((clampedValue - min) / rangeWidth) * 100;
  const rawLeft = Math.min(originPercent, valuePercent);
  const left = Math.max(0, Math.min(100, rawLeft));
  const width = Math.max(
    0,
    Math.min(100 - left, Math.abs(valuePercent - originPercent)),
  );

  return { left: `${left}%`, width: `${width}%` };
};

const CustomTrack: React.FC<TrackSlotProps> = (props) => {
  const ctxValues = useContext(SliderContext);
  const trackStyle = getTrackStyle(ctxValues);
  return <SliderTrack {...props} style={trackStyle} />;
};
CustomTrack.displayName = 'CustomTrack';

export const BaseSlider: React.FC<BaseSliderProps> = (props) => {
  const { classes } = useStyles({ disabled: Boolean(props.disabled) });
  const {
    trackOrigin = 'start',
    min,
    max,
    value,
    onChange: onChangeProp,
    onChangeCommitted: onChangeCommittedProp,
    ...rest
  } = props;
  const contextValue = useObjectMemo<SliderContextValues>({
    trackOrigin,
    min,
    max,
    value,
  });

  const onChange: MuiSliderProps['onChange'] = useCallback<OnChangeHandler>(
    (event, nextValue, activeThumb) => {
      if (Array.isArray(nextValue)) return;
      onChangeProp?.(event, nextValue, activeThumb);
    },
    [onChangeProp],
  );
  const onChangeCommitted = useCallback<OnChangeCommittedHandler>(
    (event, nextValue) => {
      if (Array.isArray(nextValue)) return;
      onChangeCommittedProp?.(event, nextValue);
    },
    [onChangeCommittedProp],
  );
  return (
    <SliderContext.Provider value={contextValue}>
      <MuiSlider
        {...rest}
        min={min}
        max={max}
        value={value}
        size="small"
        onChange={onChange}
        onChangeCommitted={onChangeCommitted}
        classes={{
          thumb: classes.thumb,
          root: classes.root,
          track: classes.track,
        }}
        slots={{ track: CustomTrack }}
        slotProps={{
          rail: { className: classes.rail },
          track: { className: classes.track },
        }}
      />
    </SliderContext.Provider>
  );
};

type SliderContextValues = {
  trackOrigin: TrackOrigin;
  min: number;
  max: number;
  value: number;
};
const SliderContext = createContext<SliderContextValues>({
  trackOrigin: 'start',
  min: 0,
  max: 0,
  value: 0,
});
